import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  ArrowLeft, 
  Edit,
  Plus,
  Trash2,
  Shield,
  Stethoscope,
  GraduationCap,
  FileCheck,
  User,
  Mail,
  Phone,
  Building,
  Calendar,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  FileText,
  Eye,
  Target
} from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import DocumentUpload from '@/components/DocumentUpload';
import { Link } from 'react-router-dom';

const StatusBadge = ({ statut }) => {
  if (statut === 'valide') return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Valide</Badge>;
  if (statut === 'bientot_echu') return <Badge className="bg-orange-100 text-orange-800"><AlertTriangle className="h-3 w-3 mr-1" />Bientôt échu</Badge>;
  if (statut === 'echu') return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Échu</Badge>;
  return <Badge variant="outline">{statut}</Badge>;
};

export default function CollaborateurDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { categoriesCACES, typesVisites, typesFormations, currentUser } = useApp();
  
  // Helper function to check if current user can edit this collaborateur
  const canEdit = () => {
    if (!currentUser || !collaborateur) return false;
    // Admin, HSE, RH can edit anyone
    if (['Admin', 'HSE', 'RH'].includes(currentUser.role)) {
      return true;
    }
    // Manager can only edit their team members
    if (currentUser.role === 'Manager') {
      return collaborateur.manager_id === currentUser.id;
    }
    return false;
  };
  
  const [collaborateur, setCollaborateur] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [cacesDialogOpen, setCacesDialogOpen] = useState(false);
  const [visiteDialogOpen, setVisiteDialogOpen] = useState(false);
  const [formationDialogOpen, setFormationDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState({ type: '', id: '', name: '' });
  
  // Documents dialog states
  const [cacesDocsDialogOpen, setCacesDocsDialogOpen] = useState(false);
  const [visiteDocsDialogOpen, setVisiteDocsDialogOpen] = useState(false);
  const [formationDocsDialogOpen, setFormationDocsDialogOpen] = useState(false);
  const [selectedCaces, setSelectedCaces] = useState(null);
  const [selectedVisite, setSelectedVisite] = useState(null);
  const [selectedFormation, setSelectedFormation] = useState(null);
  
  // Selected type formation for showing info
  const [selectedTypeFormation, setSelectedTypeFormation] = useState(null);
  
  // Form states
  const [cacesForm, setCacesForm] = useState({
    categorie_id: '',
    date_obtention: '',
    date_fin: '',
    organisme: '',
    numero_certificat: ''
  });
  
  const [visiteForm, setVisiteForm] = useState({
    type_visite_id: '',
    date_visite: '',
    date_fin: '',
    medecin: ''
  });
  
  const [formationForm, setFormationForm] = useState({
    type_formation_id: '',
    date_formation: '',
    date_fin: '',
    organisme: '',
    numero_attestation: ''
  });

  useEffect(() => {
    loadCollaborateur();
  }, [id]);

  const loadCollaborateur = async () => {
    try {
      setLoading(true);
      const data = await api.getCollaborateur(id);
      setCollaborateur(data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/collaborateurs');
    } finally {
      setLoading(false);
    }
  };

  // CACES handlers
  const handleAddCaces = async (e) => {
    e.preventDefault();
    try {
      await api.addCACES(id, cacesForm);
      toast.success('CACES ajouté');
      setCacesDialogOpen(false);
      setCacesForm({ categorie_id: '', date_obtention: '', date_fin: '', organisme: '', numero_certificat: '' });
      loadCollaborateur();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Visite handlers
  const handleAddVisite = async (e) => {
    e.preventDefault();
    try {
      await api.addVisiteMedicale(id, visiteForm);
      toast.success('Visite médicale ajoutée');
      setVisiteDialogOpen(false);
      setVisiteForm({ type_visite_id: '', date_visite: '', date_fin: '', medecin: '' });
      loadCollaborateur();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Formation handlers
  const handleTypeFormationChange = (typeId) => {
    setFormationForm({ ...formationForm, type_formation_id: typeId });
    const type = typesFormations.find(t => t.id === typeId);
    setSelectedTypeFormation(type);
    
    // Auto-calculate date_fin if date_formation is set and type has validite_mois
    if (type && formationForm.date_formation && type.validite_type === 'duree' && type.validite_mois) {
      const dateForm = new Date(formationForm.date_formation);
      dateForm.setMonth(dateForm.getMonth() + type.validite_mois);
      setFormationForm(prev => ({ ...prev, type_formation_id: typeId, date_fin: dateForm.toISOString().split('T')[0] }));
    }
  };

  const handleDateFormationChange = (date) => {
    setFormationForm({ ...formationForm, date_formation: date });
    
    if (selectedTypeFormation && selectedTypeFormation.validite_type === 'duree' && selectedTypeFormation.validite_mois) {
      const dateForm = new Date(date);
      dateForm.setMonth(dateForm.getMonth() + selectedTypeFormation.validite_mois);
      setFormationForm(prev => ({ ...prev, date_formation: date, date_fin: dateForm.toISOString().split('T')[0] }));
    }
  };

  const handleAddFormation = async (e) => {
    e.preventDefault();
    try {
      await api.addFormation(id, formationForm);
      toast.success('Formation ajoutée');
      setFormationDialogOpen(false);
      setFormationForm({ type_formation_id: '', date_formation: '', date_fin: '', organisme: '', numero_attestation: '' });
      setSelectedTypeFormation(null);
      loadCollaborateur();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Delete handler
  const handleDelete = async () => {
    try {
      if (deleteItem.type === 'caces') {
        await api.deleteCACES(deleteItem.id);
      } else if (deleteItem.type === 'visite') {
        await api.deleteVisiteMedicale(deleteItem.id);
      } else if (deleteItem.type === 'formation') {
        await api.deleteFormation(deleteItem.id);
      }
      toast.success('Supprimé avec succès');
      loadCollaborateur();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setDeleteItem({ type: '', id: '', name: '' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!collaborateur) {
    return (
      <div className="text-center py-12">
        <p>Collaborateur non trouvé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/collaborateurs')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {collaborateur.prenom} {collaborateur.nom}
            </h1>
            <p className="text-gray-500">Matricule: {collaborateur.matricule}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {['Admin', 'RH', 'Manager', 'HSE', 'Directeur'].includes(currentUser?.role) && (
            <Button variant="outline" onClick={() => navigate(`/collaborateurs/${id}/competences`)} className="gap-2">
              <Target className="h-4 w-4" />
              Compétences
            </Button>
          )}
          {canEdit() && (
            <Button onClick={() => navigate(`/collaborateurs/${id}/modifier`)} className="gap-2">
              <Edit className="h-4 w-4" />
              Modifier
            </Button>
          )}
        </div>
      </div>

      {/* Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <Mail className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{collaborateur.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <Building className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Secteur</p>
                <p className="font-medium">{collaborateur.secteur}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-purple-100">
                <User className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Poste</p>
                <p className="font-medium">{collaborateur.poste}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <Phone className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Téléphone</p>
                <p className="font-medium">{collaborateur.telephone || '-'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="caces" className="space-y-4">
        <TabsList>
          <TabsTrigger value="caces" className="gap-2">
            <Shield className="h-4 w-4" />
            CACES ({collaborateur.caces?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="visites" className="gap-2">
            <Stethoscope className="h-4 w-4" />
            Visites ({collaborateur.visites_medicales?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="formations" className="gap-2">
            <GraduationCap className="h-4 w-4" />
            Formations ({collaborateur.formations?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="autorisations" className="gap-2">
            <FileCheck className="h-4 w-4" />
            Autorisations ({collaborateur.autorisations?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* CACES Tab */}
        <TabsContent value="caces">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Certificats CACES</CardTitle>
              <Button onClick={() => setCacesDialogOpen(true)} size="sm" className="gap-2">
                <Plus className="h-4 w-4" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {collaborateur.caces?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Organisme</TableHead>
                      <TableHead>N° Certificat</TableHead>
                      <TableHead>Date obtention</TableHead>
                      <TableHead>Expiration</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {collaborateur.caces.map((caces) => (
                      <TableRow key={caces.id}>
                        <TableCell>
                          <Badge>{caces.categorie_code}</Badge>
                          <p className="text-xs text-gray-500 mt-1">{caces.categorie_libelle}</p>
                        </TableCell>
                        <TableCell>{caces.organisme}</TableCell>
                        <TableCell className="font-mono text-sm">{caces.numero_certificat}</TableCell>
                        <TableCell>{new Date(caces.date_obtention).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>{new Date(caces.date_fin).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell><StatusBadge statut={caces.statut} /></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              title="Documents"
                              onClick={() => {
                                setSelectedCaces(caces);
                                setCacesDocsDialogOpen(true);
                              }}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                setDeleteItem({ type: 'caces', id: caces.id, name: caces.categorie_code });
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun CACES enregistré</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visites Tab */}
        <TabsContent value="visites">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Visites Médicales</CardTitle>
              <Button onClick={() => setVisiteDialogOpen(true)} size="sm" className="gap-2">
                <Plus className="h-4 w-4" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {collaborateur.visites_medicales?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Médecin</TableHead>
                      <TableHead>Date visite</TableHead>
                      <TableHead>Expiration</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {collaborateur.visites_medicales.map((visite) => (
                      <TableRow key={visite.id}>
                        <TableCell>{visite.type_visite_libelle}</TableCell>
                        <TableCell>{visite.medecin || '-'}</TableCell>
                        <TableCell>{new Date(visite.date_visite).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>{new Date(visite.date_fin).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell><StatusBadge statut={visite.statut} /></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              title="Documents"
                              onClick={() => {
                                setSelectedVisite(visite);
                                setVisiteDocsDialogOpen(true);
                              }}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                setDeleteItem({ type: 'visite', id: visite.id, name: visite.type_visite_libelle });
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Stethoscope className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune visite médicale enregistrée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Formations Tab */}
        <TabsContent value="formations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Formations Internes</CardTitle>
              <Button onClick={() => setFormationDialogOpen(true)} size="sm" className="gap-2">
                <Plus className="h-4 w-4" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {collaborateur.formations?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Intitulé</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Validité</TableHead>
                      <TableHead>Expiration</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {collaborateur.formations.map((formation) => (
                      <TableRow key={formation.id}>
                        <TableCell className="font-medium">{formation.intitule}</TableCell>
                        <TableCell>{new Date(formation.date_formation).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>
                          {formation.validite_type === 'vie' ? 'À vie' : `${formation.validite_mois || '-'} mois`}
                        </TableCell>
                        <TableCell>
                          {formation.date_fin ? new Date(formation.date_fin).toLocaleDateString('fr-FR') : '-'}
                        </TableCell>
                        <TableCell><StatusBadge statut={formation.statut} /></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              title="Documents"
                              onClick={() => {
                                setSelectedFormation(formation);
                                setFormationDocsDialogOpen(true);
                              }}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                setDeleteItem({ type: 'formation', id: formation.id, name: formation.intitule });
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <GraduationCap className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune formation enregistrée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Autorisations Tab */}
        <TabsContent value="autorisations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Autorisations de Conduite</CardTitle>
              <Button onClick={() => navigate('/nouvelle-autorisation')} size="sm" className="gap-2">
                <Plus className="h-4 w-4" /> Nouvelle
              </Button>
            </CardHeader>
            <CardContent>
              {collaborateur.autorisations?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numéro</TableHead>
                      <TableHead>Catégories</TableHead>
                      <TableHead>Délivrée le</TableHead>
                      <TableHead>Expiration</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {collaborateur.autorisations.map((auth) => (
                      <TableRow key={auth.id}>
                        <TableCell className="font-mono">{auth.numero}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {(auth.categories_caces_codes || auth.categories || []).map((cat, i) => (
                              <Badge key={i} variant="outline">{cat}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {auth.signe_date ? new Date(auth.signe_date).toLocaleDateString('fr-FR') : '-'}
                        </TableCell>
                        <TableCell>
                          {auth.date_fin ? new Date(auth.date_fin).toLocaleDateString('fr-FR') : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            auth.statut === 'Délivrée' ? 'bg-green-100 text-green-800' :
                            auth.statut === 'Révoquée' || auth.statut === 'Échue' ? 'bg-red-100 text-red-800' :
                            auth.statut === 'À signer' ? 'bg-blue-100 text-blue-800' :
                            'bg-orange-100 text-orange-800'
                          }>
                            {auth.statut}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Link to={`/demandes/${auth.id}`}>
                            <Button variant="ghost" size="icon" title="Voir détails">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune autorisation enregistrée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add CACES Dialog */}
      <Dialog open={cacesDialogOpen} onOpenChange={setCacesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un CACES</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCaces}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select 
                  value={cacesForm.categorie_id} 
                  onValueChange={(v) => setCacesForm({ ...cacesForm, categorie_id: v })}
                  required
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {categoriesCACES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.code} - {cat.libelle}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date d&apos;obtention *</Label>
                  <Input type="date" value={cacesForm.date_obtention} onChange={(e) => setCacesForm({ ...cacesForm, date_obtention: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Date d&apos;expiration *</Label>
                  <Input type="date" value={cacesForm.date_fin} onChange={(e) => setCacesForm({ ...cacesForm, date_fin: e.target.value })} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Organisme *</Label>
                <Input value={cacesForm.organisme} onChange={(e) => setCacesForm({ ...cacesForm, organisme: e.target.value })} placeholder="AFPA, AFTRAL..." required />
              </div>
              <div className="space-y-2">
                <Label>N° Certificat *</Label>
                <Input value={cacesForm.numero_certificat} onChange={(e) => setCacesForm({ ...cacesForm, numero_certificat: e.target.value })} placeholder="CERT-2024-001" required />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCacesDialogOpen(false)}>Annuler</Button>
              <Button type="submit">Ajouter</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Visite Dialog */}
      <Dialog open={visiteDialogOpen} onOpenChange={setVisiteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une visite médicale</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddVisite}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Type de visite *</Label>
                <Select 
                  value={visiteForm.type_visite_id} 
                  onValueChange={(v) => setVisiteForm({ ...visiteForm, type_visite_id: v })}
                  required
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {typesVisites.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.libelle}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de visite *</Label>
                  <Input type="date" value={visiteForm.date_visite} onChange={(e) => setVisiteForm({ ...visiteForm, date_visite: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin de validité *</Label>
                  <Input type="date" value={visiteForm.date_fin} onChange={(e) => setVisiteForm({ ...visiteForm, date_fin: e.target.value })} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Médecin</Label>
                <Input value={visiteForm.medecin} onChange={(e) => setVisiteForm({ ...visiteForm, medecin: e.target.value })} placeholder="Dr. Martin" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVisiteDialogOpen(false)}>Annuler</Button>
              <Button type="submit">Ajouter</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Formation Dialog */}
      <Dialog open={formationDialogOpen} onOpenChange={(open) => {
        setFormationDialogOpen(open);
        if (!open) {
          setSelectedTypeFormation(null);
          setFormationForm({ type_formation_id: '', date_formation: '', date_fin: '', organisme: '', numero_attestation: '' });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une formation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddFormation}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Type de formation *</Label>
                <Select 
                  value={formationForm.type_formation_id} 
                  onValueChange={handleTypeFormationChange}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typesFormations.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{type.code}</Badge>
                          {type.intitule}
                          <span className="text-gray-400 text-xs ml-2">
                            ({type.validite_type === 'vie' ? 'À vie' : `${type.validite_mois} mois`})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedTypeFormation && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">{selectedTypeFormation.intitule}</p>
                      <p>Validité : {selectedTypeFormation.validite_type === 'vie' ? 'À vie' : `${selectedTypeFormation.validite_mois} mois`}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Date de formation *</Label>
                <Input 
                  type="date" 
                  value={formationForm.date_formation} 
                  onChange={(e) => handleDateFormationChange(e.target.value)} 
                  required 
                />
              </div>
              
              {selectedTypeFormation?.validite_type !== 'vie' && (
                <div className="space-y-2">
                  <Label>Date d&apos;expiration</Label>
                  <Input 
                    type="date" 
                    value={formationForm.date_fin} 
                    onChange={(e) => setFormationForm({ ...formationForm, date_fin: e.target.value })} 
                  />
                  <p className="text-xs text-gray-500">Calculée automatiquement selon le type de formation</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Organisme de formation</Label>
                <Input 
                  value={formationForm.organisme} 
                  onChange={(e) => setFormationForm({ ...formationForm, organisme: e.target.value })} 
                  placeholder="AFPA, AFTRAL..." 
                />
              </div>
              
              <div className="space-y-2">
                <Label>N° Attestation</Label>
                <Input 
                  value={formationForm.numero_attestation} 
                  onChange={(e) => setFormationForm({ ...formationForm, numero_attestation: e.target.value })} 
                  placeholder="ATT-2024-001" 
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormationDialogOpen(false)}>Annuler</Button>
              <Button type="submit">Ajouter</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{deleteItem.name}</strong> ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CACES Documents Dialog */}
      <Dialog open={cacesDocsDialogOpen} onOpenChange={setCacesDocsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Documents CACES - {selectedCaces?.categorie_code}
            </DialogTitle>
          </DialogHeader>
          {selectedCaces && (
            <DocumentUpload 
              entityType="caces" 
              entityId={selectedCaces.id}
              title="Documents du certificat"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Visite Documents Dialog */}
      <Dialog open={visiteDocsDialogOpen} onOpenChange={setVisiteDocsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Documents Visite Médicale - {selectedVisite?.type_visite_libelle}
            </DialogTitle>
          </DialogHeader>
          {selectedVisite && (
            <DocumentUpload 
              entityType="visite" 
              entityId={selectedVisite.id}
              title="Documents de la visite"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Formation Documents Dialog */}
      <Dialog open={formationDocsDialogOpen} onOpenChange={setFormationDocsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Documents Formation - {selectedFormation?.intitule}
            </DialogTitle>
          </DialogHeader>
          {selectedFormation && (
            <DocumentUpload 
              entityType="formation" 
              entityId={selectedFormation.id}
              title="Documents de la formation"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
